import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    Divider,
    Grid,
    Link,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import groupService from '../services/groupService';
import useCan from '../hooks/useCan';
import GroupContentPanel from '../components/groups/GroupContentPanel';
import GroupMembersPanel from '../components/groups/GroupMembersPanel';

/**
 * Il gruppo, per intero e in un URL.
 *
 * <p>Pagina propria e non pannello sotto l'elenco per la ragione piu' prosaica: senza un
 * `/groups/:id` un gruppo non e' linkabile, e «guarda il reparto Fatture» resta un'istruzione
 * a fare clic. `RoleDetail` lo aveva gia' risolto, e Gruppi era l'ultima pagina di
 * amministrazione a tenere la selezione in uno stato locale.</p>
 *
 * <p><b>Due colonne affiancate, non due schede.</b> Aggiungere un tipo al gruppo da' quei
 * documenti a tutti i suoi membri in un colpo solo: vederli mentre lo si fa e' l'informazione
 * che rende visibile la conseguenza, e una scheda la nasconderebbe proprio nel momento in cui
 * serve. Sotto `md` le colonne si impilano — su schermo stretto affiancarle le ridurrebbe a
 * strisce, che e' lo stesso difetto per un'altra via.</p>
 *
 * <p>La pagina e' a tutta larghezza, senza l'elenco accanto: tre colonne non ci stanno, e la
 * tabella dei membri ha bisogno di respiro (avatar, nome, email, ruolo, azione).</p>
 */
export default function GroupDetail() {
    const can = useCan();
    const { id } = useParams();
    const navigate = useNavigate();

    const [gruppo, setGruppo] = useState(null);
    const [code, setCode] = useState([]);
    const [tipi, setTipi] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [actionError, setActionError] = useState(null);
    const [anagrafica, setAnagrafica] = useState({ label: '', description: '' });

    const puoModificare = can('groups.update');

    const load = useCallback(() => {
        setLoading(true);
        setError(false);
        Promise.all([
            groupService.dettaglio(id),
            groupService.appartenenzaCode(),
            groupService.appartenenzaTipi(),
        ]).then(
            ([dettaglio, codeCatalogo, appartenenza]) => {
                setGruppo(dettaglio);
                // Le righe dell'appartenenza si rimappano nella forma che il pannello legge:
                // quei due endpoint rispondono «chi contiene cosa», non «cosa e' scegliibile»,
                // e passarle grezze fa sparire le etichette senza errori — il pannello mostra
                // gli id al posto dei nomi, che sembra un dato mancante e non un adattamento
                // dimenticato.
                setCode((codeCatalogo || []).map((riga) => ({
                    candidateGroup: riga.candidateGroup,
                    code: riga.queueName,
                })));
                setTipi((appartenenza || []).map((riga) => ({
                    id: riga.codexTypeId,
                    code: riga.codexTypeCode,
                    description: riga.codexTypeDescription,
                })));
                setLoading(false);
            },
            () => {
                setError(true);
                setLoading(false);
            },
        );
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        setAnagrafica({
            label: gruppo ? gruppo.label : '',
            description: (gruppo && gruppo.description) || '',
        });
    }, [gruppo]);

    // Ogni mutazione riuscita rilegge il gruppo dal server invece di aggiornare a mano lo
    // stato: il backend deduplica in silenzio un'aggiunta già presente, quindi due schede
    // aperte sullo stesso gruppo lascerebbero la UI a mostrare una riga che sul server non
    // è mai esistita in quella forma.
    const esegui = (promessa, messaggio) => {
        setActionError(null);
        promessa.then(
            () => groupService.dettaglio(id).then(setGruppo, () => {}),
            (err) => setActionError(err?.response?.data?.message || messaggio),
        );
    };

    // Piu' aggiunte insieme vanno una dopo l'altra, non in parallelo: sono scritture sullo
    // stesso gruppo. Un rifiuto non ferma le altre; alla fine si rilegge il gruppo e si
    // nominano le voci rimaste fuori, perche' «non e' stato possibile» senza dire quali
    // lascerebbe a indovinare chi riprovare.
    const aggiungiTutte = (voci, aggiungi, etichetta, messaggio) => {
        setActionError(null);
        const fallite = [];
        voci.reduce(
            (catena, voce) => catena.then(() => aggiungi(voce).catch(() => fallite.push(voce))),
            Promise.resolve(),
        ).then(() => {
            groupService.dettaglio(id).then(setGruppo, () => {});
            if (fallite.length > 0) {
                setActionError(`${messaggio}: ${fallite.map(etichetta).join(', ')}.`);
            }
        });
    };

    const nomeTipo = (tipoId) => (tipi.find((t) => t.id === tipoId) || {}).code || tipoId;

    const salvaAnagrafica = () => {
        setActionError(null);
        groupService.aggiorna(id, anagrafica).then(
            setGruppo,
            (err) => setActionError(
                err?.response?.data?.message || 'Non è stato possibile salvare il gruppo.',
            ),
        );
    };

    if (loading) {
        return <Typography variant="body2" color="text.secondary">Caricamento…</Typography>;
    }

    if (error || !gruppo) {
        return (
            <Box>
                <Alert
                    severity="error"
                    action={<Button color="inherit" size="small" onClick={load}>Riprova</Button>}
                >
                    Non è stato possibile caricare il gruppo.
                </Alert>
                <Button sx={{ mt: 2 }} onClick={() => navigate('/groups')}>
                    Torna ai gruppi
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} to="/groups">Gruppi</Link>
                <Typography color="text.primary">{gruppo.label}</Typography>
            </Breadcrumbs>

            {/* Titolo, pillole di conteggio e descrizione sotto: la stessa testata di
                `RoleDetail`. Le due pagine sono lo stesso genere di schermata — il dettaglio
                di un oggetto di amministrazione — e una testata diversa costringe a
                riorientarsi a ogni passaggio fra le due. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h4">{gruppo.label}</Typography>
                <Chip size="small" label={gruppo.code} />
                <Chip
                    size="small"
                    label={`${(gruppo.codexTypeIds || []).length} ${(gruppo.codexTypeIds || []).length === 1 ? 'tipo' : 'tipi'}`}
                />
                <Chip
                    size="small"
                    label={`${(gruppo.candidateGroups || []).length} ${(gruppo.candidateGroups || []).length === 1 ? 'coda' : 'code'}`}
                />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {gruppo.description || 'Nessuna descrizione.'}
            </Typography>

            {actionError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
                    {actionError}
                </Alert>
            )}

            <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={7}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Configurazione</Typography>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                        <TextField
                            size="small"
                            label="Etichetta"
                            value={anagrafica.label}
                            disabled={!puoModificare}
                            onChange={(e) =>
                                setAnagrafica((prev) => ({ ...prev, label: e.target.value }))}
                        />
                        <TextField
                            size="small"
                            fullWidth
                            label="Descrizione"
                            value={anagrafica.description}
                            disabled={!puoModificare}
                            onChange={(e) => setAnagrafica(
                                (prev) => ({ ...prev, description: e.target.value }),
                            )}
                        />
                        {puoModificare && (
                            <Button variant="outlined" onClick={salvaAnagrafica}>Salva</Button>
                        )}
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    <GroupContentPanel
                        candidateGroups={gruppo.candidateGroups}
                        codexTypeIds={gruppo.codexTypeIds}
                        codeDisponibili={code}
                        tipiDisponibili={tipi}
                        readOnly={!puoModificare}
                        onAggiungiCoda={(scelte) => aggiungiTutte(
                            scelte,
                            (coda) => groupService.aggiungiCoda(id, coda),
                            (coda) => coda,
                            'Non è stato possibile aggiungere queste code',
                        )}
                        onRimuoviCoda={(coda) => esegui(
                            groupService.rimuoviCoda(id, coda),
                            `Non è stato possibile rimuovere la coda ${coda}.`,
                        )}
                        onAggiungiTipo={(scelti) => aggiungiTutte(
                            scelti,
                            (tipo) => groupService.aggiungiTipo(id, tipo),
                            nomeTipo,
                            'Non è stato possibile aggiungere questi tipi di Codex',
                        )}
                        onRimuoviTipo={(tipo) => esegui(
                            groupService.rimuoviTipo(id, tipo),
                            'Non è stato possibile rimuovere il tipo di Codex.',
                        )}
                    />
                </Grid>

                <Grid item xs={12} md={5}>
                    <GroupMembersPanel
                        groupId={gruppo.id}
                        groupLabel={gruppo.label || gruppo.code}
                    />
                </Grid>
            </Grid>
        </Box>
    );
}
